import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiInformationCircle,
  HiSearch,
  HiChevronLeft,
  HiChevronRight,
  HiX,
} from 'react-icons/hi';
import Toast from '../../components/toaster';
import { sendFestivalNotification, getNotificationHistory } from '../../services/notificationService';
import { uploadImage } from '../../services/assetService';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const emptyForm = { title: '', description: '', image: '', other_info: '' };

const ITEMS_PER_PAGE = 15;

const historyCache = { data: null, page: 1, totalRecords: 0 };

function getPaginationRange(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, 'dots', totalPages];
  if (currentPage >= totalPages - 3) return [1, 'dots', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, 'dots', currentPage - 1, currentPage, currentPage + 1, 'dots', totalPages];
}

const Notifications = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Send modal state ───────────────────────────────────────────
  const [sendOpen, setSendOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [imageSource, setImageSource] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploadedUrl, setImageUploadedUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [originalImage, setOriginalImage] = useState('');

  // ── History state ──────────────────────────────────────────────
  const [historyData, setHistoryData] = useState(historyCache.data || []);
  const [currentPage, setCurrentPage] = useState(historyCache.page || 1);
  const [totalRecords, setTotalRecords] = useState(historyCache.totalRecords || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  const searchTimerRef = useRef(null);
  const isFirstLoad = useRef(true);

  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

  // ── Debounce search ────────────────────────────────────────────
  useEffect(() => {
    if (isFirstLoad.current) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  // ── Load history ───────────────────────────────────────────────
  const loadHistory = useCallback(async (page) => {
    setIsLoading(true);
    try {
      const body = { pagination: { page, pageSize: ITEMS_PER_PAGE, sort: { createdAt: -1 } } };
      if (debouncedSearch.trim()) body.searchtext = debouncedSearch.trim();

      const res = await getNotificationHistory(body);
      setHistoryData(res.data || []);
      setTotalRecords(res.totalCount || 0);
      historyCache.data = res.data || [];
      historyCache.page = page;
      historyCache.totalRecords = res.totalCount || 0;
    } catch (err) {
      Toast.error(err.message || 'Failed to load notification history.');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isFirstLoad.current && historyCache.data) {
      isFirstLoad.current = false;
      return;
    }
    isFirstLoad.current = false;
    loadHistory(currentPage);
  }, [currentPage, loadHistory]);

  // ── Send modal handlers ────────────────────────────────────────
  const openSendModal = () => {
    setForm(emptyForm);
    resetImageState();
    setSendOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    setImageUploadError('');
    try {
      const uploadedUrl = await uploadImage(file);
      setImageFile(file);
      setImageUploadedUrl(uploadedUrl);
      setForm((prev) => ({ ...prev, image: uploadedUrl }));
      setOriginalImage(uploadedUrl);
    } catch (err) {
      setImageUploadError(err.message || 'Failed to upload image.');
      setImageFile(null);
      setImageUploadedUrl('');
      setForm((prev) => ({ ...prev, image: '' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const resetImageState = () => {
    setImageSource('url');
    setImageFile(null);
    setImageUploadedUrl('');
    setImageUploadError('');
    setOriginalImage('');
  };

  const handleSend = async () => {
    if (!form.title.trim()) { Toast.warning('Title is required.'); return; }
    if (!form.description.trim()) { Toast.warning('Description is required.'); return; }
    setSending(true);
    try {
      const payload = { title: form.title, description: form.description };
      if (form.image.trim()) payload.image = form.image.trim();
      if (form.other_info.trim()) payload.other_info = form.other_info.trim();
      await sendFestivalNotification(payload);
      Toast.success('Festival notification sent successfully!');
      setSendOpen(false);
      historyCache.data = null;
      loadHistory(1);
    } catch (err) {
      Toast.error(err.message || 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-screen bg-primary overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-primary sticky top-0 px-4 py-4 md:px-6 md:pt-6 md:pb-4">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <HiMenu className="w-6 h-6 text-gray-800" />
            </button>
            <h1 className="text-xl md:text-3xl font-bold text-gray-800">Notifications</h1>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:pb-6">
          <div className="flex flex-col gap-4">

            {/* Search + Button bar */}
            <div className="mb-5 md:mb-4 mt-4 md:mt-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                {/* Search */}
                <div className="relative w-full md:w-40">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                {/* Send button */}
                <button
                  onClick={openSendModal}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap self-start md:self-auto"
                >
                  <span className="text-base font-bold">+</span>
                  <span>Send Notification</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">

              {totalRecords > 0 && (
                <div className="px-4 py-2 border-b border-gray-100 flex items-center">
                  <span className="text-xs text-gray-500">
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)} of {totalRecords}
                  </span>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell w-20">Image</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Sent At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} className="py-16 text-center">
                          <div className="flex justify-center">
                            <svg className="animate-spin h-6 w-6 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          </div>
                        </td>
                      </tr>
                    ) : historyData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-16 text-center text-gray-400 text-sm">
                          No notifications found.
                        </td>
                      </tr>
                    ) : (
                      historyData.map((item) => (
                        <tr key={item._id} onClick={() => setSelectedItem(item)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                          <td className="px-4 py-3 text-gray-800 font-medium">{item.title}</td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                            <span title={item.description}>
                              {item.description?.length > 80 ? `${item.description.slice(0, 80)}…` : item.description}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            {item.image ? (
                              <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden lg:table-cell whitespace-nowrap">
                            {new Date(item.createdAt).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    {getPaginationRange(currentPage, totalPages).map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">...</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage ? 'bg-brand text-white shadow-sm hover:opacity-90' : 'text-gray-900 hover:bg-gray-100'}`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Send Modal ── */}
      {sendOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSendOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">Send Festival Notification</h3>
              <button onClick={() => setSendOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                <HiX size={18} />
              </button>
            </div>

            <div className="px-6 py-5 flex flex-col gap-5">
              {/* Title */}
              <div>
                <label className="text-sm font-bold text-gray-500 block mb-1.5">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. Happy Diwali! 🪔"
                  className="w-full h-11 px-3 bg-[#ECECEC] border-2 border-transparent rounded-lg text-black font-semibold text-sm focus:outline-none focus:border-brand/30 placeholder:font-normal placeholder:text-gray-400"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-bold text-gray-500 block mb-1.5">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="e.g. May this festival of lights bring joy and wisdom to your family."
                  rows={3}
                  className="w-full px-3 py-2.5 bg-[#ECECEC] border-2 border-transparent rounded-lg text-black font-semibold text-sm focus:outline-none focus:border-brand/30 placeholder:font-normal placeholder:text-gray-400 resize-none"
                />
              </div>

              {/* Image */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-bold text-gray-500">
                    Image <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'}`}>
                        {imageSource === 'url' ? 'Switch left to choose a file from your device' : 'Switch right to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = imageSource === 'url' ? 'file' : 'url';
                        setImageSource(next);
                        setImageFile(null);
                        setImageUploadedUrl('');
                        setImageUploadError('');
                        setForm((prev) => ({ ...prev, image: originalImage }));
                      }}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {imageSource === 'url' ? (
                  <div>
                    <input
                      type="url"
                      name="image"
                      value={form.image}
                      onChange={(e) => {
                        const val = e.target.value;
                        setForm((prev) => ({ ...prev, image: val }));
                        setOriginalImage(val);
                      }}
                      placeholder="https://cdn.example.com/banner.jpg"
                      className="w-full h-11 px-3 bg-[#ECECEC] border-2 border-transparent rounded-lg text-black font-semibold text-sm focus:outline-none focus:border-brand/30 placeholder:font-normal placeholder:text-gray-400"
                    />
                    {form.image && (
                      <div className="mt-2">
                        <img src={form.image} alt="Preview" className="w-full h-32 object-cover rounded-lg" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => { const file = e.target.files[0]; if (file) handleImageUpload(file); }}
                      disabled={uploadingImage}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent border-gray-300 ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {uploadingImage && (
                      <div className="mt-2 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-xs text-gray-600">Uploading image...</span>
                      </div>
                    )}
                    {imageUploadError && <p className="text-red-500 text-xs mt-1">{imageUploadError}</p>}
                    {(imageUploadedUrl || imageFile || form.image) && !uploadingImage && (
                      <div className="mt-2">
                        <img
                          src={imageUploadedUrl || (imageFile ? URL.createObjectURL(imageFile) : form.image)}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Other Info */}
              <div>
                <label className="text-sm font-bold text-gray-500 block mb-1.5">
                  Other Info <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  name="other_info"
                  value={form.other_info}
                  onChange={handleChange}
                  placeholder="e.g. Vedify team wishes you a blessed Diwali."
                  className="w-full h-11 px-3 bg-[#ECECEC] border-2 border-transparent rounded-lg text-black font-semibold text-sm focus:outline-none focus:border-brand/30 placeholder:font-normal placeholder:text-gray-400"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  onClick={() => setSendOpen(false)}
                  className="h-9 px-5 text-sm font-bold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || uploadingImage}
                  className="flex items-center gap-2 h-9 px-6 text-sm font-bold text-white rounded-lg bg-brand disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? (<><Spinner />Sending...</>) : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedItem(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">Notification Details</h3>
              <button onClick={() => setSelectedItem(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                <HiX size={18} />
              </button>
            </div>

            <div className="px-6 py-5 flex flex-col gap-5">
              {selectedItem.image && (
                <img src={selectedItem.image} alt="" className="w-full h-48 object-cover rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
              )}

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Title</p>
                <p className="text-sm font-semibold text-gray-800">{selectedItem.title}</p>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Description</p>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedItem.description}</p>
              </div>

              {selectedItem.other_info && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Other Info</p>
                  <p className="text-sm text-gray-700">{selectedItem.other_info}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Sent At</p>
                <p className="text-sm text-gray-700">
                  {new Date(selectedItem.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">ID</p>
                <p className="text-xs text-gray-400 font-mono break-all">{selectedItem._id}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
